import {
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  FormGroupDirective,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, of, Subscription } from 'rxjs';
import { defaultIfEmpty, finalize, switchMap } from 'rxjs/operators';
import { Acl } from 'src/app/models/acl';
import { EnumAffinities } from 'src/app/models/gum-models/enum-affinities';
import { ExternalParameters } from 'src/app/models/external-parameters';
import { Organization } from 'src/app/models/organization.model';
import { Property } from 'src/app/models/property';
import { User } from 'src/app/models/user';
import { ExternalsParametersService } from 'src/app/services/external-parameters.service';
import { GumOrganizationsService } from 'src/app/services/gumservices/gum-organizations.service';
import { GumUsersService } from 'src/app/services/gumservices/gum-users.service';
import { MatButton } from '@angular/material/button';
import { GumPropertiesComponent } from '../../users/gum-properties/gum-properties.component';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatTabGroup, MatTab } from '@angular/material/tabs';

import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { GumOrganizationModel } from 'src/app/models/gum-models/gum-organization.model';
import { MatCheckboxModule } from '@angular/material/checkbox';

@Component({
  selector: 'app-gum-organization-form',
  templateUrl: './gum-organization-form.component.html',
  styleUrl: './gum-organization-form.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    MatTabGroup,
    MatTab,
    MatCard,
    MatCardContent,
    MatFormField,
    MatLabel,
    MatInput,
    GumPropertiesComponent,
    MatButton,
    MatAutocompleteModule,
    NgxSpinnerModule,
    MatCheckboxModule
],
})
export class GumOrganizationFormComponent implements OnInit, OnDestroy {
  private spinnerService = inject(NgxSpinnerService);

  organizationEnabled = true;

  /** Profile de l'utilisateur connecté */
  currentUser!: User;

  /** Informations de l'organization */
  organizationInformations!: Organization;
  organizationProperties: Property[] = [];
  organizationRights: Acl[] = [];

  /** Formulaire de l'organisation */
  organizationForm: UntypedFormGroup;

  isCreation!: boolean;

  organizationList!: Organization[];

  /** Indique si les valeur des propriétés sont valides */
  propertiesValid!: boolean;

  affinity = EnumAffinities.Organization;

  @Input() organization!: Organization;

  @ViewChild(FormGroupDirective, { static: false })
  formGroupDirective!: FormGroupDirective;

  isExternal = false;
  externalParameter!: ExternalParameters;
  externalParametersChange!: Subscription;

  private internalRefRegex = new RegExp(
    /^(?:\d+|[a-zA-Z0-9]*\d+[a-zA-Z0-9]*)$/
  );

  filteredOrganizations: Organization[] = [];

  constructor(
    private route: ActivatedRoute,
    private organizationService: GumOrganizationsService,
    private formbuilder: UntypedFormBuilder,
    private router: Router,
    private toastr: ToastrService,
    private usersService: GumUsersService,
    private externalsParametersService: ExternalsParametersService
  ) {
    this.organizationForm = new UntypedFormGroup({});
  }

  ngOnDestroy(): void {
    this.externalParametersChange.unsubscribe();
  }

  /** A l'initialisation du composant */
  ngOnInit() {
    // Récupère les informations de l'utilisateur connecté
    this.usersService.getSessionCurrentUser().subscribe((user) => {
      if (user) {
        this.currentUser = user;
      } else {
        this.toastr.error("Impossible de récupérer l'utilisateur courant.");
        this.router.navigate(['/organizations']);
      }
    });

    // Récupère l'identifiant de l'organisation en paramètre
    this.route.queryParams.subscribe((params) => {
      const organizationId = params['id'];
      this.loadDatas(organizationId);
    });

    this.externalParametersChange =
      this.externalsParametersService.externalParametersChange$.subscribe(
        (parameters) => {
          if (parameters) {
            this.isExternal = true;
            this.externalParameter = parameters;
          }
        }
      );
  }

  /** Lors du changement des propriétés */
  onPropertiesChanged(organizationProperties: Property[]) {
    this.organizationProperties = organizationProperties;
  }

  /** Lors du changement des droits */
  onRightsChanged(organizationRights: Acl[]) {
    this.organizationRights = organizationRights;
  }

  onParentSelected(org: Organization) {
    // met à jour le vrai champ technique
    this.organizationForm.get('generalGroup.organizationId')?.setValue(org.id);
  }

  /** Click sur le bouton Enregistrer */
  onSubmit() {
    this.spinnerService.show('spinner');

    // Met à jour les infos du modèle à partir du formulaire
    this.commitChanges();

    const values = this.organizationForm.get('generalGroup')!.value;

    // Construction du payload GUM → côté backend Nexus
    const payload: GumOrganizationModel = {
      Name: values.display,
      Siret: values.siret,
      Internalref: values.internalRef,
      Email: values.email,
      Phone: values.phone,
      Address: values.address,
      Zipcode: values.zipCode,
      City: values.city,
      Country: values.country,
      Nexustype: 'Partenaire',
      IsActive: this.organizationEnabled,
      parentid: this.isCreation
        ? this.organizationInformations.organizationId // injecté depuis navbar
        : values.organizationId, // readonly sinon
    };

    // CAS CRÉATION
    if (this.isCreation) {
      this.organizationService
        .addOrganization(payload)
        .pipe(finalize(() => this.spinnerService.hide('spinner')))
        .subscribe({
          next: () => {
            this.toastr.success('Partenaire créé avec succès.');
            this.router.navigate(['/organizations']);
          },
        });

      return;
    }

    // CAS MODIFICATION
    this.organizationService
      .updateOrganization(this.organizationInformations.id, payload)
      .pipe(
        switchMap((organization) => {
          const requests = this.getPropertiesAndRightsRequests(organization.id);
          if (requests.length === 0) return of(null);
          return forkJoin(requests).pipe(defaultIfEmpty(null));
        }),
        switchMap(() =>
          this.organizationService.getOrganizationWithDisplayParentOrganization(
            this.organizationInformations.id
          )
        ),
        finalize(() => this.spinnerService.hide('spinner'))
      )
      .subscribe({
        next: () => {
          this.toastr.success('Partenaire modifié avec succès.');
          this.router.navigate(['/organizations']);
        },
      });
  }

  /** création du formulaire */
  createForm(organization: Organization) {
    const getProp = (name: string): string => {
      return organization.properties?.find((p) => p.name === name)?.value ?? '';
    };
    this.organizationForm = this.formbuilder.group({
      generalGroup: this.formbuilder.group({
        organizationDisplaySearch: [''],
        organizationId: [
          organization.organizationId ?? '',
          Validators.required,
        ],
        displayParentOrganization: [
          organization.displayParentOrganization ?? '',
        ],
        display: [organization.display ?? '', Validators.required],
        siret: [
          getProp('siret'),
          [Validators.required, Validators.pattern(/^\d{9,14}$/)],
        ],
        internalRef: [getProp('internalRef'), Validators.required],
        email: [getProp('email'), [Validators.required, Validators.email]],
        phone: [getProp('phone'), Validators.required],
        address: [getProp('address'), Validators.required],
        zipCode: [getProp('zipCode'), Validators.required],
        city: [getProp('city'), Validators.required],
        country: [getProp('country'), Validators.required],
      }),
    });

    this.organizationForm
      .get('generalGroup.organizationDisplaySearch')
      ?.valueChanges.subscribe((input) => {
        const filterText =
          typeof input === 'string'
            ? input.toLowerCase()
            : typeof input === 'object' && input?.display
            ? input.display.toLowerCase()
            : '';

        this.filteredOrganizations = this.organizationList.filter((org) =>
          org.display?.toLowerCase().includes(filterText)
        );
      });
  }

  /** Application des changements */
  commitChanges() {
    const values = this.organizationForm.get('generalGroup')?.value;

    this.organizationInformations.display = values.display;

    if (this.isCreation) {
      this.organizationInformations.organizationId = values.organizationId;
    }

    this.organizationProperties = [
      { name: 'siret', value: values.siret, legacy: false },
      { name: 'internalRef', value: values.internalRef, legacy: false },
      { name: 'email', value: values.email, legacy: false },
      { name: 'phone', value: values.phone, legacy: false },
      { name: 'address', value: values.address, legacy: false },
      { name: 'zipCode', value: values.zipCode, legacy: false },
      { name: 'city', value: values.city, legacy: false },
      { name: 'country', value: values.country, legacy: false },
      { name: 'nexus.type', value: 'Partenaire', legacy: false },
    ];
  }

  getPropertiesAndRightsRequests(OrganizationId: string): any[] {
    let requests: any[] = [];

    // Recherche des propriétés à créer ou mettre à jour
    this.organizationProperties.forEach((p) => {
      const userProperty = this.organizationInformations.properties.filter(
        (up) => up.name === p.name
      );
      if (userProperty.length > 0) {
        // Vérifie si la valeur a changée
        if (userProperty[0].value !== p.value) {
          requests.push(
            this.organizationService.updateOrganizationProperty(
              OrganizationId,
              p.name,
              p.value
            )
          );
        }
      } else {
        // N'existe pas, à ajouter
        requests.push(
          this.organizationService.updateOrganizationProperty(
            OrganizationId,
            p.name,
            p.value
          )
        );
      }
    });

    // Recherche des propriétés à supprimer
    this.organizationInformations.properties.forEach((p) => {
      if (!this.organizationProperties.find((up) => up.name === p.name)) {
        // A supprimer
        requests.push(
          this.organizationService.deleteOrganizationProperty(
            OrganizationId,
            p.name
          )
        );
      }
    });

    // Recherche des droits à créer ou mettre à jour
    this.organizationRights.forEach((r) => {
      const userRight = this.organizationInformations.rights.filter(
        (ur) => ur.name === r.name
      );
      if (userRight.length > 0) {
        // Vérifie si la valeur a changée
        if (userRight[0].value !== r.value) {
          requests.push(
            this.organizationService.updateOrganizationRight(
              OrganizationId,
              r.name,
              r.value
            )
          );
        }
      } else {
        // N'existe pas, à ajouter
        requests.push(
          this.organizationService.updateOrganizationRight(
            OrganizationId,
            r.name,
            r.value
          )
        );
      }
    });

    // Recherche des droits à supprimer
    this.organizationInformations.rights.forEach((r) => {
      if (!this.organizationRights.find((ur) => ur.name === r.name)) {
        // A supprimer
        requests.push(
          this.organizationService.deleteOrganizationRight(
            OrganizationId,
            r.name
          )
        );
      }
    });

    return requests;
  }

  loadDatas(organizationId: string) {
    this.spinnerService.show('spinner');

    //  CAS MODIFICATION
    if (organizationId) {
      this.organizationService
        .getOrganizationWithDisplayParentOrganization(organizationId)
        .pipe(
          switchMap((x) => {
            this.organizationInformations = x;
            this.organizationRights = this.organizationInformations.rights;
            this.organizationProperties =
              this.organizationInformations.properties;
            this.isCreation = false;
            this.organizationEnabled = this.organizationInformations.enabled;

            return this.organizationService.getOrganisations();
          })
        )
        .subscribe({
          next: (organizations) => {
            this.organizationList = organizations;

            this.createForm(this.organizationInformations);

            this.spinnerService.hide('spinner');
          },
          error: () => {
            this.spinnerService.hide('spinner');
          },
        });
      return;
    }

    //  CAS CRÉATION (pas d'ID)
    this.isCreation = true;
    this.organizationInformations = new Organization();

    // On récupère l’organisation sélectionnée dans la navbar
    const currentParent = this.organizationService.getCurrentOrganization();

    // Injection du parent dans le modèle
    this.organizationInformations.organizationId = currentParent?.id ?? '';
    this.organizationInformations.displayParentOrganization =
      currentParent?.display ?? '';

    // Maintenant on récupère toutes les organisations
    this.organizationService.getOrganisations().subscribe({
      next: (organizations) => {
        this.organizationList = organizations.sort(
          (a, b) => a.display?.localeCompare(b.display ?? '') ?? 0
        );

        this.createForm(this.organizationInformations);

        this.spinnerService.hide('spinner');
      },
      error: () => {
        this.spinnerService.hide('spinner');
      },
    });
  }

  cancelButtonClick() {
    if (this.isExternal) {
      window.location.href = this.externalParameter.return ?? '/organizations';
    } else {
      this.router.navigate(['/organizations']);
    }
  }
}
